import { useState } from 'react';
import { MoreHorizontal, Radio } from 'lucide-react';
import { useIntegrations, useDisconnectIntegration } from '@/hooks';
import { integrationService } from '@/services';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { confirmDisconnect } from './disconnect-confirm.store';
import type { Integration } from '../types';

/** Kim's built-in helpdesk: nothing to fill in, one button. Can sit alongside one external helpdesk. */
export const KimHelpdeskContent = ({ integration }: { integration: Integration }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const handleConnect = async () => {
		setIsSubmitting(true);
		try {
			await integrationService.connectKimHelpdesk();
			await refetch();
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to connect Kim Helpdesk. Please try again.'));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected) {
		return (
			<div className='flex flex-col gap-4 px-5 pb-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name}</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm' aria-label='Manage connection'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
					<Label className='text-xs text-muted-foreground'>Status</Label>
					<span className='text-sm font-medium'>Connected</span>
				</div>
			</div>
		);
	}

	return (
		<div className='flex flex-col h-full'>
			<div className='flex flex-col gap-1 px-5'>
				<h3 className='text-sm font-semibold'>Turn on Kim Helpdesk</h3>
				<p className='text-sm text-muted-foreground'>
					No credentials needed. Runs alongside your external helpdesk.
				</p>
			</div>
			<DialogFooter className='px-5 pb-5 pt-4'>
				<Button type='button' className='w-full' size='lg' disabled={isSubmitting} onClick={handleConnect}>
					<Radio className='size-4' />
					{isSubmitting ? 'Connecting…' : 'Connect Kim Helpdesk'}
				</Button>
			</DialogFooter>
		</div>
	);
};
